import { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';

import {
  trans,
  getPagePath,
  useSoyuzRequest,
  clientErrorTracker
} from '@spotahome/soyuz/client';
import SoyuzAnalytics from '@spotahome/soyuz-analytics';

import {
  HeaderContainer,
  FooterSimplified,
  Button,
  Spinner
} from '@spotahome/ui-library';

import '@spotahome/landings-common/src/scss/init.scss';

import { Mediator } from '@spotahome/soyuz-mediator';

import BrandsBanner from '@spotahome/ui-library/src/tenant/components/HeroReferrals/BrandsBanner';
import HeroReferrals from '@spotahome/ui-library/src/tenant/components/HeroReferrals';
import HowReferralsWorkRevamp from '@spotahome/ui-library/src/tenant/components/HowReferralsWorkRevamp';
import CopyCodeIllustration from '@spotahome/ui-library/src/tenant/components/SvgImages/CopyCodeIllustration';
import EnvelopeIllustration from '@spotahome/ui-library/src/tenant/components/SvgImages/EnvelopeIllustration';
import CouponIllustration from '@spotahome/ui-library/src/tenant/components/SvgImages/CouponIllustratrion';

import LazyLoginSignUpModal from '@spotahome/landings-common/src/components/LoginSignupModal';

import graphqlClient from '@spotahome/landings-common/src/graphql/client';
import { redeemReferralCodeByAccountId } from '@spotahome/landings-common/src/graphql/mutations/referee';
import { getReferrerDetailsByCode } from '@spotahome/landings-common/src/graphql/queries/referral';
import { getLoggedInUserInfo } from '@spotahome/landings-common/src/api/auth-client';

import './RefereeContainerRevamp.scss';

const redirectToHome = () => {
  window.location = getPagePath('landing');
};

const getCreateAccountContent = (onClickCTA, hasError, isLoading) => (
  <>
    <Button
      onClick={() => onClickCTA('tenant')}
      color="secondary"
      className="referee-revamp__cta-tenant"
      disabled={isLoading || hasError}
      fullWidth
    >
      {isLoading && <Spinner small />}
      {trans('contact-us.tenant')}
    </Button>
    <Button
      onClick={() => onClickCTA('landlord')}
      color="secondary"
      disabled={isLoading || hasError}
      fullWidth
    >
      {isLoading && <Spinner small />}
      {trans('contact-us.landlord')}
    </Button>
  </>
);

const getHowItWorksSteps = (onClickCTA, hasError, isLoading) => [
  {
    title: trans('refer-a-friend.how-it-works.create-account'),
    content: isLoading ? (
      <Spinner small />
    ) : (
      getCreateAccountContent(onClickCTA, hasError, isLoading)
    ),
    image: <CopyCodeIllustration />
  },
  {
    title: trans('refer-a-friend.how-it-works.book-or-publish.title'),
    content: trans('refer-a-friend.how-it-works.book-or-publish.content'),
    image: <EnvelopeIllustration />
  },
  {
    title: trans('refer-a-friend.how-it-works.win.title'),
    content: trans('refer-a-friend.how-it-works.win.content'),
    image: <CouponIllustration />
  }
];

// TODO: rename this if experiment 'ReferralsProgramRevamp' is rolled out
const RefereeContainerRevamp = ({ loggedIn = false }) => {
  const [hasError, setHasError] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const { params } = useSoyuzRequest();
  const referrerCode = params.referrerCode;

  const redeemReferralCode = useCallback(async () => {
    await redeemReferralCodeByAccountId(graphqlClient())({
      referralCode: referrerCode
    });
  }, [referrerCode]);

  const redeemReferralCodeAndRedirectToHome = useCallback(async () => {
    try {
      await redeemReferralCode();
      redirectToHome();
    } catch (error) {
      setHasError(true);
    }
  }, [redeemReferralCode]);

  const handleModalOpen = () => {
    Mediator.publish('AUTH_MODAL_OPEN', {
      initialAction: 'signup'
    });
  };

  useEffect(() => {
    const getActiveUserRedeemAndRedirect = async () => {
      setIsLoading(true);
      try {
        const { data: user } = await getLoggedInUserInfo();
        if (user.firstLogin) {
          redeemReferralCodeAndRedirectToHome();
        } else {
          redirectToHome();
        }
      } catch (error) {
        setHasError(true);
      }
    };
    if (loggedIn) {
      getActiveUserRedeemAndRedirect();
    }
  }, [loggedIn, redeemReferralCodeAndRedirectToHome]);

  useEffect(() => {
    const getRefererDetailsByCode = async () => {
      try {
        await getReferrerDetailsByCode(graphqlClient())({
          input: { referralCode: referrerCode }
        });
      } catch (error) {
        clientErrorTracker.error({
          error,
          source: 'RefereeContainerRevamp-getRefererDetailsByCode'
        });

        setHasError(true);
      }
      setIsLoading(false);
    };
    getRefererDetailsByCode();
  }, [referrerCode]);

  const handleProfileChoice = async profileChoice => {
    SoyuzAnalytics.sendGA4Event('referral_cta_click', {
      value: profileChoice,
      section: 'referee'
    });
    if (profileChoice === 'landlord') {
      window.location = getPagePath('externals.landlord-landing-signup', {
        query: { referrerCode }
      });
      return;
    }

    handleModalOpen();
  };

  return (
    <div>
      <HeaderContainer
        login={false}
        showMobileMenu={false}
        links={{
          home: false,
          listYourProperty: false,
          howItWorks: false,
          favorites: false
        }}
      />
      <div className="referee-revamp__content">
        <section className="referee-revamp__block">
          <HeroReferrals
            brandsBannerLayout="row"
            title={
              hasError
                ? `${trans('referee.landing.error.title')} ${trans(
                    'referee.landing.error.message.codeNotValid'
                  )}`
                : null
            }
          />
        </section>
        <section className="referee-revamp__brands-banner">
          <BrandsBanner centered />
        </section>
        <HowReferralsWorkRevamp
          className="referee-revamp__block"
          steps={getHowItWorksSteps(handleProfileChoice, hasError, isLoading)}
          titleSize="large"
        />
      </div>
      <LazyLoginSignUpModal
        onTraditionalSignupSuccess={redeemReferralCodeAndRedirectToHome}
        onTraditionalLoginSuccess={redirectToHome}
      />
      <FooterSimplified />
    </div>
  );
};

RefereeContainerRevamp.propTypes = {
  loggedIn: PropTypes.bool
};

export default RefereeContainerRevamp;
